/**
 * StructuredFormMasterPanel — submissões agrupadas por aluno.
 *
 * Para cada aluno que submeteu, mostra todos os campos+valores. Lista
 * por aluno (anonimizado), não por campo, pra mestre acompanhar a
 * forma do raciocínio de cada um. Em PR-3 vai agrupar por equipe quando
 * scope='team'.
 */

import { useMemo, useState } from 'react'

import { useAdapter } from '../../../adapter'
import { labApi } from '../../../api/labApi'
import type { MissionComponentProps } from '../../../games/atlas/components'
import { useParticipants } from '../../../hooks/useParticipants'
import { useLabComponentState } from './useLabComponentState'

interface Response {
  membershipId?: string
  fieldId?: string
  value?: string
  teamId?: string | null
}

interface FormField {
  id: string
  label: string
}

export function StructuredFormMasterPanel(props: MissionComponentProps) {
  const adapter = useAdapter()
  const slideIndex = props.slideIndex ?? 0
  const snap = useLabComponentState('lab.form.structured', slideIndex)
  const config = props.slideConfig ?? {}
  const fields: FormField[] = Array.isArray(config.fields)
    ? (config.fields as FormField[])
    : []
  const responses = (snap?.aggregate.responses as Response[]) ?? []
  const total = snap?.totalPlayers ?? 0
  const scope = (config.scope as string) || 'student'
  const assignmentBy = (config.assignmentBy as string) || undefined
  const fieldsByAssignment = (config.fieldsByAssignment as Record<string, FormField[]>) || {}
  const isTeamMode = scope === 'team' && assignmentBy === 'team'

  if (isTeamMode) {
    return (
      <TeamAssignmentPanel
        slideIndex={slideIndex}
        fieldsByAssignment={fieldsByAssignment}
        sessionId={adapter.sessionId}
        responses={responses}
      />
    )
  }

  // Agrupa por membership_id (em PR-3 troca por team_id quando definido).
  const byAuthor: Record<string, Response[]> = {}
  for (const r of responses) {
    const key = r.membershipId ?? 'unknown'
    byAuthor[key] = byAuthor[key] ?? []
    byAuthor[key].push(r)
  }
  const authors = Object.keys(byAuthor)

  function labelFor(fieldId: string | undefined): string {
    const f = fields.find((x) => x.id === fieldId)
    return f?.label ?? String(fieldId)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Submissões por aluno</h3>
        <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85em' }}>
          {authors.length} / {total} aluno(s) submeteu
        </p>
      </header>

      {authors.length === 0 && (
        <div style={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.9em' }}>
          Aguardando submissões…
        </div>
      )}

      {authors.map((author) => (
        <div
          key={author}
          style={{
            padding: 10,
            background: 'var(--color-lab-bg-2, #F2F2ED)',
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ opacity: 0.6, fontSize: '0.75em' }}>
            aluno {author.slice(0, 8)}
          </div>
          {byAuthor[author].map((r, i) => (
            <div key={`${r.fieldId}-${i}`} style={{ fontSize: '0.85em' }}>
              <span style={{ fontWeight: 500 }}>{labelFor(r.fieldId)}: </span>
              <span style={{ whiteSpace: 'pre-wrap' }}>{r.value}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}


// =========================================================================
// Painel de atribuição de equipes (scope=team + assignmentBy=team)
// =========================================================================
//
// Mestre vê:
//  - Lista de equipes declaradas em fieldsByAssignment
//  - Pra cada equipe, seletor multi de membros (players)
//  - Botão Atribuir que chama labApi.assignTeams
//  - Lista de respostas agrupadas por equipe após atribuição

interface TeamAssignmentPanelProps {
  slideIndex: number
  fieldsByAssignment: Record<string, FormField[]>
  sessionId: string
  responses: Response[]
}

function TeamAssignmentPanel(props: TeamAssignmentPanelProps) {
  const { slideIndex, fieldsByAssignment, sessionId, responses } = props
  const teamIds = Object.keys(fieldsByAssignment)
  const players = useParticipants('players')
  // Map team_id -> Set<membership_id> selecionado pelo mestre
  const [selection, setSelection] = useState<Record<string, Set<string>>>(() => {
    const init: Record<string, Set<string>> = {}
    for (const id of teamIds) init[id] = new Set()
    return init
  })
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [statusMsg, setStatusMsg] = useState<string>('')

  function toggle(teamId: string, membershipId: string) {
    setSelection((prev) => {
      const next = { ...prev }
      // remove de todas as equipes primeiro (1 membro = 1 equipe)
      for (const tid of teamIds) {
        const s = new Set(next[tid])
        s.delete(membershipId)
        next[tid] = s
      }
      // adiciona na equipe atual
      const tgt = new Set(next[teamId])
      tgt.add(membershipId)
      next[teamId] = tgt
      return next
    })
  }

  function teamOf(membershipId: string): string | null {
    for (const tid of teamIds) {
      if (selection[tid].has(membershipId)) return tid
    }
    return null
  }

  async function handleSubmit() {
    setStatus('sending')
    setStatusMsg('')
    try {
      const assignments: Record<string, { payload: Record<string, unknown>; membership_ids: string[] }> = {}
      for (const tid of teamIds) {
        assignments[tid] = {
          payload: { fields: fieldsByAssignment[tid] },
          membership_ids: Array.from(selection[tid]),
        }
      }
      const res = await labApi.assignTeams(sessionId, { slide_index: slideIndex, assignments })
      setStatus('done')
      setStatusMsg(`${res.memberships_notified} membership(s) notificado(s)`)
    } catch (err) {
      setStatus('error')
      setStatusMsg(String((err as Error).message ?? err))
    }
  }

  const responsesByAuthor = useMemo(() => {
    const map: Record<string, Response[]> = {}
    for (const r of responses) {
      const k = r.membershipId ?? 'unknown'
      map[k] = map[k] ?? []
      map[k].push(r)
    }
    return map
  }, [responses])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Atribuição de equipes</h3>
        <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85em' }}>
          {players.length} player(s); {teamIds.length} equipe(s)
        </p>
      </header>

      {teamIds.map((tid) => {
        const tFields = fieldsByAssignment[tid] ?? []
        return (
          <section
            key={tid}
            style={{
              padding: 10,
              background: 'var(--color-lab-bg-2, #F2F2ED)',
              borderRadius: 6,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <strong style={{ fontSize: '0.9em' }}>{tid}</strong>
              <span style={{ opacity: 0.6, fontSize: '0.8em' }}>
                {selection[tid].size} membro(s) · {tFields.length} campo(s)
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {players.map((p) => {
                const assignedTo = teamOf(p.membershipId)
                const isThis = assignedTo === tid
                const elsewhere = assignedTo && !isThis
                return (
                  <button
                    key={p.membershipId}
                    onClick={() => toggle(tid, p.membershipId)}
                    title={elsewhere ? `atribuído à equipe '${assignedTo}'` : ''}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.75em',
                      border: '1px solid var(--color-lab-rule, #D8D5CB)',
                      borderRadius: 4,
                      background: isThis ? 'var(--color-lab-accent, #5B2DB8)' : 'white',
                      color: isThis ? 'white' : 'inherit',
                      cursor: 'pointer',
                      opacity: elsewhere ? 0.4 : 1,
                    }}
                  >
                    {p.displayName ?? p.membershipId.slice(0, 6)}
                  </button>
                )
              })}
            </div>
          </section>
        )
      })}

      <button
        onClick={handleSubmit}
        disabled={status === 'sending'}
        style={{
          padding: '8px 16px',
          background: 'var(--color-lab-accent, #5B2DB8)',
          color: 'white',
          border: 'none',
          borderRadius: 4,
          cursor: status === 'sending' ? 'default' : 'pointer',
          fontSize: 'var(--text-lab-base, 1rem)',
        }}
      >
        {status === 'sending' ? 'Enviando…' : 'Atribuir equipes'}
      </button>
      {statusMsg && (
        <div
          style={{
            fontSize: '0.85em',
            color: status === 'error' ? 'var(--color-lab-rose, #D4474A)' : '#0a7837',
          }}
        >
          {statusMsg}
        </div>
      )}

      {responses.length > 0 && (
        <section>
          <div style={{ fontSize: '0.85em', fontWeight: 500, marginBottom: 6 }}>
            Respostas das equipes
          </div>
          {Object.entries(responsesByAuthor).map(([author, rs]) => (
            <div
              key={author}
              style={{
                padding: 8,
                background: 'var(--color-lab-bg-2, #F2F2ED)',
                borderRadius: 6,
                marginBottom: 6,
                fontSize: '0.8em',
              }}
            >
              <div style={{ opacity: 0.6, marginBottom: 4 }}>
                {teamOf(author) ?? '(sem equipe)'} · {author.slice(0, 8)}
              </div>
              {rs.map((r, i) => (
                <div key={`${r.fieldId}-${i}`}>
                  <strong>{r.fieldId}:</strong> {r.value}
                </div>
              ))}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}
